// Migrations-Übersicht für das Preismodell. Standardmäßig die aktiv zahlenden
// Kunden (dieselbe Definition wie die Kennzahlen), auf Wunsch alle Partner.
// Je Zeile Vertrag, Laufzeit, Status der Pricing-Sperre und Nutzung auf
// Abruf. CS stellt von hier aus Kunde für Kunde auf ein Paket um (Link auf
// /customer/:id#paket). Unterdashboards sind eigene Partner und stehen als
// eigene Zeile da.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, HomeIcon } from '@heroicons/react/20/solid'
import { format } from 'date-fns'

import { query } from '../api'
import Loader from '../components/Loader'
import Button from '../components/Button'
import { Notice } from '../components/experiments/ExperimentBadges'
import { usageMeterModel } from '../components/pricing/UsageMeter'
import PricingEnforcementBadge, {
  PaymentKindBadge,
  contractOf,
  recommendationLabel
} from '../components/pricing/PricingEnforcementBadge'
import { StageBadge } from '../components/pricing/PricingOverrideControl'
import SchemaProbeNotice from '../components/pricing/SchemaProbeNotice'
import { classNames, extractErrorMessage } from '../helper'
import { STAGE } from '../helper/stage'
import {
  effectiveLimitsOf,
  formatCount,
  recommendPlan
} from '../helper/pricing'
import {
  describePricingFlag,
  pricingEnforcementState,
  pricingFlagOf
} from '../helper/pricing-flag'
import {
  planOf,
  isCreditPayment,
  selectPayments
} from '../helper/pricing-mirror/payment-selection'
import { LIMIT_STATES } from '../helper/pricing-mirror/limits'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import {
  pricingUsageQueryOptions,
  toPricingUsage
} from '../hooks/use-pricing-usage'
import { useSchemaCapabilities } from '../hooks/use-schema-capabilities'

// Gleichzeitige Zählungen bei „Alle prüfen". Jede Zählung lädt Stellen,
// Talente und Zugänge eines Partners.
export const CHECK_CONCURRENCY = 2

export const RESOURCES = ['careers', 'seats', 'talentStorage']

const RESOURCE_SHORT = {
  careers: 'Stellen',
  seats: 'Zugänge',
  talentStorage: 'Talente'
}

// ---------------------------------------------------------------------------
// Reine Logik (für Tests exportiert)
// ---------------------------------------------------------------------------

/**
 * Wie isActivePaidPayment in deletePartner (Backend partnerControl). Nicht
 * Trial und entweder status ACTIVE oder expires_at in der Zukunft.
 */
export const isActivePaidPayment = (payment, nowMs = Date.now()) => {
  if (!payment || payment.is_trial === true) return false
  if (payment.status === 'ACTIVE') return true
  const expires = Date.parse(payment.expires_at)
  return Number.isFinite(expires) && expires > nowMs
}

export const groupPaymentsByPartner = (payments) => {
  const groups = {}
  ;(Array.isArray(payments) ? payments : []).forEach((payment) => {
    if (!payment?.partner_id) return
    if (!groups[payment.partner_id]) groups[payment.partner_id] = []
    groups[payment.partner_id].push(payment)
  })
  return groups
}

const nameOf = (partner) =>
  partner.display_name || partner.name || partner.id || ''

/**
 * Eine Zeile je Partner, sortiert nach Name.
 *
 * Migriert heißt wie im Dashboard, es gibt ein Plan-Payment (planPayment aus
 * selectPayments). Ob es abgelaufen ist, spielt für die Sperre keine Rolle,
 * planExpired zeigt es nur an. Aktiv zahlend zählt Credit-Payments nicht mit,
 * das Dashboard ignoriert sie für die Laufzeit.
 *
 * @param {{ partners: object[], payments: object[], flag: object|null|undefined,
 *   stage?: string, now?: Date, plansKnown?: boolean }} params flag wie
 *   pricingFlagOf (undefined heißt nicht lesbar). plansKnown false heißt, die
 *   Payments sind ohne plan geladen (Schema-Probe fehlgeschlagen).
 */
export const buildMigrationRows = ({
  partners,
  payments,
  flag,
  stage = STAGE,
  now = new Date(),
  plansKnown = true
}) => {
  const byPartner = groupPaymentsByPartner(payments)
  const nowMs = now.getTime()

  return (Array.isArray(partners) ? partners : [])
    .filter((partner) => partner?.id)
    .map((partner) => {
      const list = byPartner[partner.id] || []
      const { planPayment, legacyModePayment, periodPayment } =
        selectPayments(list)
      const contract = contractOf(list)
      const governing = planPayment || legacyModePayment
      const activePaying = list.some(
        (payment) =>
          !isCreditPayment(payment) && isActivePaidPayment(payment, nowMs)
      )
      const trialEnd = Date.parse(periodPayment?.expires_at)

      return {
        id: partner.id,
        name: nameOf(partner),
        partner,
        payments: list,
        contract,
        planPayment,
        planId: planPayment ? planOf(planPayment) : null,
        periodPayment,
        periodEnd: periodPayment?.expires_at || null,
        // Kein partner, das PARTNER-Set kennt lead_ampel nicht. Targeting
        // darauf ergibt dann partial statt eines falschen Ergebnisses.
        enforcement: pricingEnforcementState({
          flag,
          partnerId: partner.id,
          payments: list,
          stage,
          plansKnown
        }),
        flagPercent:
          flag !== undefined
            ? describePricingFlag(flag, partner.id, { stage }).percent
            : null,
        limits: governing ? effectiveLimitsOf(governing) : null,
        activePaying,
        migrated: !!planPayment,
        planExpired: !!planPayment && !isActivePaidPayment(planPayment, nowMs),
        runningTrial:
          !activePaying &&
          !!periodPayment?.is_trial &&
          Number.isFinite(trialEnd) &&
          trialEnd > nowMs,
        // Wie runningTrial über die Laufzeit-Zahlung. contractOf zieht ein
        // älteres Plan-Payment vor, ein neuerer Trial bliebe sonst sichtbar.
        isTrial: !!periodPayment?.is_trial
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'de'))
}

/**
 * Kennzahlen. Mit Paket heißt migriert, also ein Plan-Payment wie im
 * Dashboard, auch wenn es abgelaufen ist. Altvertrag sind die übrigen aktiv
 * zahlenden. Trials sind laufende Trials ohne aktive Zahlung. Der Anteil wird
 * abgerundet, damit 100 % erst erscheinen, wenn wirklich alle umgestellt
 * sind. Ohne lesbare Pakete (plansKnown false) sind die Paket-Zahlen null.
 */
export const migrationKpis = (rows, { plansKnown = true } = {}) => {
  const paying = rows.filter((row) => row.activePaying)
  const withPlan = paying.filter((row) => row.migrated).length
  return {
    activePaying: paying.length,
    withPlan: plansKnown ? withPlan : null,
    legacy: plansKnown ? paying.length - withPlan : null,
    trials: rows.filter((row) => row.runningTrial).length,
    percent: !plansKnown
      ? null
      : paying.length
        ? Math.floor((withPlan / paying.length) * 100)
        : 0
  }
}

export const kpiLine = (kpis) =>
  kpis.withPlan === null
    ? `Aktiv zahlend: ${kpis.activePaying} · mit Paket: unbekannt · zu migrieren: unbekannt · Trials: ${kpis.trials}`
    : `Aktiv zahlend: ${kpis.activePaying} · mit Paket: ${kpis.withPlan} (${kpis.percent} %) · zu migrieren: ${kpis.legacy} · Trials: ${kpis.trials}`

/**
 * Nutzung einer Zeile gegen ihre Limits. Über Limit zählt nur bei Kunden mit
 * Paket, denn nur dort setzt die Sperre die Grenzen durch. Altverträge
 * zeigen ihre Rohwerte nur zur Orientierung.
 *
 * @param {object} row aus buildMigrationRows
 * @param {object|null} usage aus toPricingUsage
 */
export const usageStatusOf = (row, usage) => {
  if (!usage) {
    return { checked: false, states: {}, overLimit: [], lockedTalents: 0 }
  }

  const states = {}
  RESOURCES.forEach((resource) => {
    states[resource] = usageMeterModel({
      used: usage[resource],
      limit: row.limits ? row.limits[resource] : null
    }).state
  })

  return {
    checked: true,
    states,
    overLimit: row.planPayment
      ? RESOURCES.filter(
          (resource) => states[resource] === LIMIT_STATES.OVER_LIMIT
        )
      : [],
    lockedTalents: row.planPayment
      ? usage.lockedTalents(row.limits?.talentStorage)
      : 0
  }
}

// Filter mit derselben Definition wie die Kennzahlen (migrationKpis), damit
// die Zahl am Filter und die Zahl der Zeilen übereinstimmen
export const MIGRATION_FILTERS = [
  {
    id: 'active',
    label: 'Aktiv zahlend',
    hint: 'Kunden mit aktiver bezahlter Zahlung, wie bei Löschen im Backend'
  },
  {
    id: 'to_migrate',
    label: 'Zu migrieren',
    hint: 'Aktiv zahlend, aber ohne aktive Paket-Zahlung'
  },
  {
    id: 'with_plan',
    label: 'Mit Paket',
    hint: 'Aktiv zahlend mit aktiver Paket-Zahlung'
  },
  {
    id: 'trials',
    label: 'Trials',
    hint: 'Laufende Trials ohne aktive bezahlte Zahlung'
  },
  {
    id: 'over_limit',
    label: 'Über Limit',
    hint: 'Nur geprüfte Kunden mit Paket, deren Nutzung über einer Grenze liegt'
  },
  {
    id: 'all',
    label: 'Alle Partner',
    hint: 'Auch Partner ohne Zahlung und mit abgelaufenen Verträgen'
  }
]

export const DEFAULT_MIGRATION_FILTER = 'active'

const matchesFilter = (row, filter, usageById) => {
  switch (filter) {
    case 'active':
      return row.activePaying
    case 'to_migrate':
      return row.activePaying && !row.migrated
    case 'with_plan':
      return row.activePaying && row.migrated
    case 'trials':
      return row.runningTrial
    case 'over_limit':
      return usageStatusOf(row, usageById[row.id]).overLimit.length > 0
    default:
      return true
  }
}

/**
 * Zahl der Zeilen je Filter, ohne Suche und ohne „Trials ausblenden".
 * active, to_migrate, with_plan und trials entsprechen migrationKpis.
 */
export const filterCounts = (rows, usageById = {}) =>
  Object.fromEntries(
    MIGRATION_FILTERS.map(({ id }) => [
      id,
      rows.filter((row) => matchesFilter(row, id, usageById)).length
    ])
  )

export const filterMigrationRows = (
  rows,
  {
    filter = DEFAULT_MIGRATION_FILTER,
    hideTrials = false,
    search = '',
    usageById = {}
  } = {}
) => {
  const needle = search.trim().toLowerCase()

  return rows.filter((row) => {
    if (hideTrials && row.isTrial) return false
    if (!matchesFilter(row, filter, usageById)) return false
    if (!needle) return true
    return [row.name, row.partner?.name, row.id].some((value) =>
      String(value || '')
        .toLowerCase()
        .includes(needle)
    )
  })
}

/**
 * Arbeitet items mit höchstens limit gleichzeitigen Aufrufen ab. Fehler
 * einzelner Aufrufe brechen nicht ab. shouldStop beendet nach dem laufenden
 * Aufruf.
 *
 * @returns {Promise<{ ok: boolean, value?: unknown, error?: unknown }[]>}
 *   in der Reihenfolge der items, nicht gestartete bleiben undefined
 */
export const runWithConcurrency = async (
  items,
  limit,
  worker,
  { shouldStop = () => false, onSettled } = {}
) => {
  const list = Array.isArray(items) ? items : []
  const results = new Array(list.length)
  let next = 0

  const runner = async () => {
    while (next < list.length && !shouldStop()) {
      const index = next
      next += 1
      try {
        results[index] = { ok: true, value: await worker(list[index], index) }
      } catch (error) {
        results[index] = { ok: false, error }
      }
      if (onSettled) onSettled(results[index], index)
    }
  }

  const size = Math.max(1, Math.min(Number(limit) || 1, list.length))
  await Promise.all(Array.from({ length: size }, runner))
  return results
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

const STATE_TEXT = {
  [LIMIT_STATES.OK]: 'text-green-700',
  [LIMIT_STATES.WARNING]: 'text-amber-700',
  [LIMIT_STATES.AT_LIMIT]: 'text-red-700',
  [LIMIT_STATES.OVER_LIMIT]: 'text-red-700 font-semibold'
}

const formatDate = (iso) => {
  if (!iso) return '-'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd.MM.yyyy')
}

const UsageCell = ({ row, usageResult, onCheck }) => {
  const usage = toPricingUsage(usageResult?.data)
  const status = usageStatusOf(row, usage)
  const isFetching = !!usageResult?.isFetching

  if (!usage) {
    if (isFetching) {
      return <span className='text-xs text-gray-500'>Wird gezählt ...</span>
    }
    return (
      <div className='space-y-1'>
        {usageResult?.isError && (
          <p
            className='text-xs text-red-700'
            title={extractErrorMessage(usageResult.error)}
          >
            Zählen fehlgeschlagen
          </p>
        )}
        <button
          type='button'
          onClick={onCheck}
          className='text-xs font-medium text-indigo-600 hover:text-indigo-500'
        >
          {usageResult?.isError ? 'Erneut prüfen' : 'Nutzung prüfen'}
        </button>
      </div>
    )
  }

  return (
    <div className='flex items-start gap-2'>
      <ul className='space-y-0.5 text-xs tabular-nums'>
        {RESOURCES.map((resource) => {
          const limit = row.limits ? row.limits[resource] : null
          const showLimit =
            !!row.planPayment && typeof limit === 'number' && limit > 0
          return (
            <li
              key={resource}
              className={
                row.planPayment
                  ? STATE_TEXT[status.states[resource]] || 'text-gray-600'
                  : 'text-gray-600'
              }
            >
              {RESOURCE_SHORT[resource]} {formatCount(usage[resource])}
              {showLimit && ` / ${formatCount(limit)}`}
            </li>
          )
        })}
        {status.lockedTalents > 0 && (
          <li className='font-medium text-red-700'>
            {status.lockedTalents === 1
              ? '1 Talent gesperrt'
              : `${formatCount(status.lockedTalents)} Talente gesperrt`}
          </li>
        )}
        {!row.planPayment && (
          <li className='text-gray-400'>ohne Paket nicht durchgesetzt</li>
        )}
      </ul>
      <button
        type='button'
        onClick={onCheck}
        disabled={isFetching}
        title='Neu zählen'
        className='text-gray-400 hover:text-gray-600 disabled:opacity-50'
      >
        <ArrowPathIcon
          className={classNames('h-4 w-4', isFetching && 'animate-spin')}
          aria-hidden='true'
        />
        <span className='sr-only'>Neu zählen</span>
      </button>
    </div>
  )
}

const RecommendationCell = ({ row, usageResult }) => {
  const usage = toPricingUsage(usageResult?.data)
  if (!usage) return <span className='text-gray-400'>-</span>

  const recommendation = recommendPlan({
    careers: usage.careers,
    seats: usage.seats,
    storage: usage.talentStorage
  })
  const fits =
    !!row.planId &&
    recommendation.planId === row.planId &&
    usageStatusOf(row, usage).overLimit.length === 0

  return (
    <span className='text-xs text-gray-700'>
      {fits ? 'Paket passt' : recommendationLabel(recommendation)}
    </span>
  )
}

const Breadcrumb = () => (
  <nav className='flex mb-8' aria-label='Breadcrumb'>
    <ol className='flex items-center space-x-4'>
      <li>
        <Link to='/' className='text-gray-400 hover:text-gray-500'>
          <HomeIcon className='h-5 w-5 flex-shrink-0' aria-hidden='true' />
          <span className='sr-only'>Home</span>
        </Link>
      </li>
      <li>
        <div className='flex items-center'>
          <svg
            className='h-5 w-5 flex-shrink-0 text-gray-300'
            fill='currentColor'
            viewBox='0 0 20 20'
            aria-hidden='true'
          >
            <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
          </svg>
          <span
            className='ml-4 text-sm font-medium text-gray-500'
            aria-current='page'
          >
            Pricing-Migration
          </span>
        </div>
      </li>
    </ol>
  </nav>
)

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const PricingMigration = () => {
  const queryClient = useQueryClient()
  const capabilities = useSchemaCapabilities()
  const withPlan = capabilities.paymentPlan
  // Bei fehlgeschlagener Probe sind die Zahlungen ohne plan geladen, jede
  // Paket-Zahlung sähe wie ein Altvertrag aus
  const plansKnown = !capabilities.isError

  const [filter, setFilter] = useState(DEFAULT_MIGRATION_FILTER)
  const [hideTrials, setHideTrials] = useState(false)
  const [search, setSearch] = useState('')
  const [progress, setProgress] = useState(null)
  // Jeder Lauf von „Alle prüfen" hat eine eigene ID. Nur der aktuelle darf
  // Fortschritt schreiben, ein gestoppter hört nach dem laufenden Aufruf auf.
  const runIdRef = useRef(0)
  const runningRef = useRef(null)

  useEffect(
    () => () => {
      runIdRef.current += 1
    },
    []
  )

  // Gleicher Key wie die Kundenliste, also gemeinsamer Cache
  const partnersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => query({ query: 'listPartners' })
  })

  const paymentsQuery = useQuery({
    queryKey: ['pricing-migration-payments', withPlan],
    queryFn: () =>
      query({ query: withPlan ? 'listPaymentsWithPlan' : 'listPayments' }),
    // Erst nach der Probe, sonst liefe die Liste einmal ohne plan
    enabled: !capabilities.isLoading
  })

  const { flagsQuery } = useFeatureFlags()
  const flag = flagsQuery.isError ? undefined : pricingFlagOf(flagsQuery.data)

  const rows = useMemo(
    () =>
      buildMigrationRows({
        partners: partnersQuery.data,
        payments: paymentsQuery.data,
        flag,
        plansKnown
      }),
    [partnersQuery.data, paymentsQuery.data, flag, plansKnown]
  )

  // Nur lesen. Gezählt wird auf Abruf über fetchQuery, die Zeilen sehen das
  // Ergebnis über den gemeinsamen Cache (auch von der Kundenseite).
  const usageResults = useQueries(
    rows.map((row) => ({
      ...pricingUsageQueryOptions(row.id),
      enabled: false
    }))
  )
  const usageResultById = {}
  const usageById = {}
  rows.forEach((row, index) => {
    usageResultById[row.id] = usageResults[index]
    usageById[row.id] = toPricingUsage(usageResults[index]?.data)
  })

  const visibleRows = filterMigrationRows(rows, {
    filter,
    hideTrials,
    search,
    usageById
  })
  const kpis = migrationKpis(rows, { plansKnown })
  const counts = filterCounts(rows, usageById)

  const checkOne = (partnerId) =>
    queryClient
      .fetchQuery({ ...pricingUsageQueryOptions(partnerId), staleTime: 0 })
      .catch(() => null)

  const checkAll = async () => {
    // Ein gestoppter Lauf ist noch nicht zurück, sonst liefen beide parallel
    if (runningRef.current) return
    const pending = visibleRows.filter((row) => !usageById[row.id])
    if (!pending.length) return

    runIdRef.current += 1
    const runId = runIdRef.current
    const isCurrent = () => runIdRef.current === runId
    setProgress({
      runId,
      done: 0,
      total: pending.length,
      running: true,
      stopping: false
    })

    const run = runWithConcurrency(
      pending,
      CHECK_CONCURRENCY,
      (row) => queryClient.fetchQuery(pricingUsageQueryOptions(row.id)),
      {
        shouldStop: () => !isCurrent(),
        onSettled: () => {
          if (!isCurrent()) return
          setProgress((current) =>
            current?.runId === runId
              ? { ...current, done: current.done + 1 }
              : current
          )
        }
      }
    )
    runningRef.current = run

    try {
      await run
    } finally {
      runningRef.current = null
      setProgress((current) =>
        current?.runId === runId
          ? { ...current, running: false, stopping: false }
          : current
      )
    }
  }

  const stopChecking = () => {
    // Neue ID, der laufende Lauf ist damit nicht mehr aktuell
    runIdRef.current += 1
    setProgress((current) =>
      current ? { ...current, running: false, stopping: true } : current
    )
  }

  if (
    partnersQuery.isLoading ||
    capabilities.isLoading ||
    paymentsQuery.isLoading
  ) {
    return <Loader loadingText='Kunden und Zahlungen werden geladen ...' />
  }

  const loadError = partnersQuery.isError
    ? { what: 'Kunden', query: partnersQuery }
    : paymentsQuery.isError
      ? { what: 'Zahlungen', query: paymentsQuery }
      : null

  if (loadError) {
    return (
      <div>
        <Breadcrumb />
        <Notice tone='error'>
          {loadError.what} konnten nicht geladen werden.{' '}
          {extractErrorMessage(loadError.query.error)}{' '}
          <button
            type='button'
            onClick={() => loadError.query.refetch()}
            className='font-medium underline'
          >
            Erneut versuchen
          </button>
        </Notice>
      </div>
    )
  }

  const unchecked = visibleRows.filter((row) => !usageById[row.id]).length

  return (
    <div>
      <Breadcrumb />

      <div className='sm:flex sm:items-start sm:justify-between'>
        <div>
          <div className='flex items-center gap-3'>
            <h1 className='text-base font-semibold leading-6 text-gray-900'>
              Pricing-Migration
            </h1>
            <StageBadge stage={STAGE} />
          </div>
          <p className='mt-2 max-w-3xl text-sm text-gray-700'>
            Standardmäßig siehst du die aktiv zahlenden Kunden, gezählt wie die
            Kennzahlen. Alle Partner zeigt auch Partner ohne Zahlung und mit
            abgelaufenen Verträgen. Umgestellt wird auf der Kundenseite mit
            einer neuen Zahlung, die bisherige bleibt als Rollback stehen.
          </p>
          <p className='mt-3 text-sm font-medium text-gray-900'>
            {kpiLine(kpis)}
          </p>
        </div>
      </div>

      <div className='mt-4 space-y-2'>
        {flagsQuery.isError && (
          <Notice tone='error'>
            FEATURE_FLAGS konnte nicht geladen werden, der Status der
            Pricing-Sperre ist deshalb unbekannt.{' '}
            <button
              type='button'
              onClick={() => flagsQuery.refetch()}
              className='font-medium underline'
            >
              Erneut laden
            </button>
          </Notice>
        )}
        {flagsQuery.data?.parseError && (
          <Notice tone='error'>
            FEATURE_FLAGS enthält kein gültiges JSON, der Status der
            Pricing-Sperre ist deshalb unbekannt.
          </Notice>
        )}
        {capabilities.isError ? (
          <SchemaProbeNotice
            onRetry={capabilities.refetch}
            isRetrying={capabilities.isFetching}
          >
            Ob diese Umgebung Payment.plan kennt, ist unklar. Die Zahlungen sind
            ohne plan geladen. Pakete fehlen deshalb in der Übersicht, die
            Kennzahlen zu Paketen und die Filter „Zu migrieren“ und „Mit Paket“
            sind nicht belastbar.
          </SchemaProbeNotice>
        ) : (
          !capabilities.paymentPlan && (
            <Notice tone='info'>
              Diese Umgebung kennt Payment.plan noch nicht. Bis zum
              Backend-Deploy gelten alle Zahlungen als Altvertrag.
            </Notice>
          )
        )}
      </div>

      <div className='mt-6 flex flex-wrap items-center gap-4'>
        <div
          className='inline-flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300'
          role='group'
          aria-label='Filter'
        >
          {MIGRATION_FILTERS.map((option, index) => (
            <button
              key={option.id}
              type='button'
              title={option.hint}
              aria-pressed={filter === option.id}
              onClick={() => setFilter(option.id)}
              className={classNames(
                'px-3 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600',
                index === 0 && 'rounded-l-md',
                index === MIGRATION_FILTERS.length - 1 && 'rounded-r-md',
                index > 0 && 'border-l border-gray-300',
                filter === option.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-900 hover:bg-gray-50'
              )}
            >
              {option.label}
              {counts[option.id] !== undefined && (
                <span className='ml-1.5 tabular-nums opacity-80'>
                  {counts[option.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={hideTrials}
            onChange={(e) => setHideTrials(e.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
          />
          Trials ausblenden
        </label>

        <div className='w-60'>
          <label htmlFor='pricing-migration-search' className='sr-only'>
            Suche
          </label>
          <input
            id='pricing-migration-search'
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Kunde suchen'
            className='block w-full rounded-md border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
          />
        </div>

        <div className='ml-auto flex items-center gap-3'>
          {progress && (
            <span className='text-sm text-gray-600 tabular-nums'>
              {progress.done} von {progress.total} geprüft
            </span>
          )}
          {progress?.running ? (
            <Button.SecondaryBase text='Stoppen' onClick={stopChecking} />
          ) : progress?.stopping ? (
            <Button.SecondaryBase text='Wird gestoppt ...' disabled />
          ) : (
            <Button.PrimaryBase
              text={`Alle prüfen (${unchecked})`}
              onClick={checkAll}
              disabled={unchecked === 0}
            />
          )}
        </div>
      </div>

      <div className='mt-4 flow-root'>
        <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
            <table className='min-w-full divide-y divide-gray-300'>
              <thead>
                <tr>
                  {[
                    'Kunde',
                    'Vertrag',
                    'Laufzeit bis',
                    'Pricing-Sperre',
                    'Nutzung',
                    'Empfehlung',
                    ''
                  ].map((label, index) => (
                    <th
                      key={label || index}
                      scope='col'
                      className={classNames(
                        'py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap',
                        index === 0 ? 'pl-4 pr-3 sm:pl-0' : 'px-3'
                      )}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {visibleRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className='py-8 text-center text-sm text-gray-500'
                    >
                      Keine Kunden für diesen Filter.
                    </td>
                  </tr>
                )}
                {visibleRows.map((row) => (
                  <tr key={row.id} data-testid='migration-row'>
                    <td className='py-4 pl-4 pr-3 text-sm sm:pl-0'>
                      <p className='font-medium text-gray-900'>{row.name}</p>
                      <p className='text-xs text-gray-400'>{row.id}</p>
                    </td>
                    <td className='px-3 py-4 text-sm'>
                      <PaymentKindBadge payment={row.contract.payment} />
                      {row.planExpired && (
                        <p
                          className='mt-1 text-xs text-red-600'
                          data-testid='plan-expired'
                        >
                          Paket abgelaufen, die Sperre greift weiter
                        </p>
                      )}
                    </td>
                    <td
                      className={classNames(
                        'whitespace-nowrap px-3 py-4 text-sm',
                        Date.parse(row.periodEnd) < Date.now()
                          ? 'text-red-600'
                          : 'text-gray-500'
                      )}
                    >
                      {formatDate(row.periodEnd)}
                    </td>
                    <td className='px-3 py-4 text-sm'>
                      <PricingEnforcementBadge
                        state={row.enforcement}
                        percent={row.flagPercent}
                        isLoading={flagsQuery.isLoading}
                      />
                    </td>
                    <td className='px-3 py-4 text-sm'>
                      <UsageCell
                        row={row}
                        usageResult={usageResultById[row.id]}
                        onCheck={() => checkOne(row.id)}
                      />
                    </td>
                    <td className='px-3 py-4 text-sm'>
                      <RecommendationCell
                        row={row}
                        usageResult={usageResultById[row.id]}
                      />
                    </td>
                    <td className='whitespace-nowrap px-3 py-4 text-right text-sm'>
                      <Link
                        to={`/customer/${row.id}#paket`}
                        className='font-medium text-indigo-600 hover:text-indigo-500'
                      >
                        Öffnen
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingMigration
